import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Input,
    Select,
    Space,
    Tag,
    message,
    Modal,
    Tooltip
} from 'antd';
import {SearchOutlined, ExportOutlined, EyeOutlined} from '@ant-design/icons';
import {useQuery, useMutation, useLazyQuery} from '@apollo/client';
import {GET_BILL_LISTING} from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {
    BATCH_CONFIRM_REVENUE_BILLS,
    EXPORT_BILL
} from '../../../graphql/mutations/settlement/settlementBillMutations';
import {GET_EXPORT_HISTORY} from '../../../graphql/queries/settlement/billExportQueries';
import {BillTimeRangePicker} from '../../../components/settlement/BillTimeRangePicker';
import {ClientSugSelector} from '../../../components/settlement/ClientSugSelector';
import {BillExportModal} from '../../../components/settlement/BillExportModal';
import type {ExportLanguage} from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {renderBillTypeTag, getBillTypeLabel} from '../../../utils/billTypeConfig';
import {formatTimeWithTimezone, formatDateWithTimezone} from '../../../utils/timezoneFormatter';
import {canExportBill, getExportButtonTitle} from '../../../utils/billExportPermission';
import {getBillStatusColor, getBillStatusLabel} from '../../../utils/billI18n';
import type {
    SettlementBillType,
    BillFiltersInput,
    BillAmountRangeInput,
    PageRequestTypeInput,
    BillExportTemplateType
} from '../../../graphql/types/settlement';
import {BILL_LIST_DEFAULT_PAGE_REQUEST, FrontendBillStatusEnum} from '../../../graphql/types/settlement/CommonTypes';
import { useTranslation } from 'react-i18next';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from '../../../constants/resourceCodes';
import { hasResource } from '../../../utils/resourceAuth';
import {
    consumeSettlementBillListState,
    persistBeforeBillDetailNavigation
} from './settlementBillListState';

const {Option} = Select;

// 客户账单页覆盖的账单类型（收入侧）：运费、SLA、分拣
export const CLIENT_BILL_TYPES = ['DELIVERY_FEE_BILL', 'SLA_BILL', 'SORTING_BILL'];

const CLIENT_DEFAULT_FILTERS: BillFiltersInput = {
    includeCanceled: false,
    includeDeleted: true,
    billTypes: [...CLIENT_BILL_TYPES]
};

const REVENUE_BATCH_CONFIRMABLE_STATUSES = new Set([
    'INTERNAL_ALREADY',
    'ADJUSTMENT_PENDING',
    'EXTERNAL_REJECTED'
]);

function getClientListInitialState() {
    const saved = consumeSettlementBillListState('client');
    if (saved) {
        return saved;
    }
    return {
        filters: {...CLIENT_DEFAULT_FILTERS},
        tempFilters: {...CLIENT_DEFAULT_FILTERS},
        pageRequest: {...BILL_LIST_DEFAULT_PAGE_REQUEST},
        billIdInput: '',
        selectedTimeZone: 'Asia/Shanghai'
    };
}

function normalizeBillAmountRangeForQuery(
    range: BillAmountRangeInput | undefined
): BillAmountRangeInput | undefined {
    if (!range) return undefined;
    const min = range.minAmount?.trim() ?? '';
    const max = range.maxAmount?.trim() ?? '';
    if (!min && !max) return undefined;
    return {
        minAmount: min || undefined,
        maxAmount: max || undefined,
    };
}

/**
 * 账单列表页面
 * 基于 billing_service_frontend_scheduler_plan.md 方案实现
 */
export const SettlementBillList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [initialState] = useState(getClientListInitialState);
    const [filters, setFilters] = useState<BillFiltersInput>(initialState.filters);
    const [tempFilters, setTempFilters] = useState<BillFiltersInput>(initialState.tempFilters);
    const [pageRequest, setPageRequest] = useState<PageRequestTypeInput>(initialState.pageRequest);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [exportTaskId, setExportTaskId] = useState<string | null>(null);
    const [selectedBill, setSelectedBill] = useState<SettlementBillType | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // 临时存储账单ID输入值（不立即触发查询）
    const [billIdInput, setBillIdInput] = useState<string>(initialState.billIdInput);

    // 存储用户选择的时区（用于显示账单周期）
    const [selectedTimeZone, setSelectedTimeZone] = useState<string>(
        initialState.selectedTimeZone ?? 'Asia/Shanghai'
    );

    // GraphQL 查询 - 不使用 skip，让它初始就执行
    const {data, loading, refetch} = useQuery(GET_BILL_LISTING, {
        variables: {filters, pageRequest},
        fetchPolicy: 'cache-and-network' // 先返回缓存（如果有），同时发起网络请求
    });


    // 监听账单数据更新事件
    useEffect(() => {
        const handleBillDataUpdated = () => {
            refetch();
        };

        window.addEventListener('billDataUpdated', handleBillDataUpdated);

        return () => {
            window.removeEventListener('billDataUpdated', handleBillDataUpdated);
        };
    }, [refetch]);

    // 导出账单变更
    const [exportBill] = useMutation(EXPORT_BILL);
    const [batchConfirmRevenueBills, {loading: batchConfirmLoading}] =
        useMutation(BATCH_CONFIRM_REVENUE_BILLS);
    const canOperate = hasResource([
        ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE
    ]);

    // 懒加载查询导出历史
    const [checkExportHistory] = useLazyQuery(GET_EXPORT_HISTORY, {
        fetchPolicy: 'network-only'
    });

    // 处理账单类型筛选（选具体类型时用 billType，清空时回退到本页覆盖的全部类型）
    const handleBillTypeChange = (value: string | undefined) => {
        setTempFilters(prev => ({
            ...prev,
            billType: value as any,
            billTypes: value ? undefined : [...CLIENT_BILL_TYPES]
        }));
    };

    // 处理账单状态筛选
    const handleBillStatusChange = (value: string) => {
        setTempFilters(prev => ({
            ...prev,
            billStatus: value as any
        }));
    };

    // 处理时间范围筛选
    const handleTimeRangeChange = (timeRange: any) => {
        setTempFilters(prev => ({
            ...prev,
            billTimeRange: timeRange
        }));

        // 同时保存用户选择的时区，用于显示账单周期
        if (timeRange?.timeZoneId) {
            setSelectedTimeZone(timeRange.timeZoneId);
        }
    };

    // 处理分页变化
    const handlePageChange = (page: number, size: number) => {
        setPageRequest((prev) => ({...prev, page, size}));
    };

    // 处理查询操作
    const handleSearch = useCallback(() => {
        console.log('查询操作开始:', {billIdInput, tempFilters});

        if (billIdInput && billIdInput.trim() !== '') {
            const newFilters: BillFiltersInput = {
                billId: billIdInput.trim(),
                includeCanceled: tempFilters.includeCanceled,
                includeDeleted: tempFilters.includeDeleted,
                isSent: tempFilters.isSent,
                invoiceStatus: tempFilters.invoiceStatus,
                billAmountRange: normalizeBillAmountRangeForQuery(tempFilters.billAmountRange),
                clientIds: tempFilters.clientIds,
                billType: tempFilters.billType,
                billTypes: tempFilters.billTypes,
                billStatus: tempFilters.billStatus,
                billTimeRange: tempFilters.billTimeRange
            };
            if (!newFilters.billType && !newFilters.billTypes?.length) {
                newFilters.billTypes = [...CLIENT_BILL_TYPES];
            }
            console.log('按账单ID查询:', newFilters);
            setFilters(newFilters);
        } else {
            const newFilters: BillFiltersInput = {
                ...tempFilters,
                billId: undefined,
                billAmountRange: normalizeBillAmountRangeForQuery(tempFilters.billAmountRange)
            };
            if (!newFilters.billType && !newFilters.billTypes?.length) {
                newFilters.billTypes = [...CLIENT_BILL_TYPES];
            }
            console.log('按筛选条件查询:', newFilters);
            setFilters(newFilters);
        }
        // 重置页码到第一页
        setPageRequest((prev) => ({...prev, page: 1, size: 20}));
    }, [billIdInput, tempFilters]);

    // 处理查看详情
    const handleViewDetail = (bill: SettlementBillType) => {
        persistBeforeBillDetailNavigation('client', {
            filters,
            tempFilters,
            pageRequest,
            billIdInput,
            selectedTimeZone
        });
        navigate(`/settlement/bill-detail/${bill.id}`);
    };

    const handleBatchInternalConfirm = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先勾选要确认的账单');
            return;
        }

        let remark = '';
        Modal.confirm({
            title: '批量内部确认账单',
            content: (
                <div className="pb-6">
                    <div className="mb-2">
                        确定要对已选中的 {selectedRowKeys.length} 张收入侧账单执行内部确认吗？
                    </div>
                    <div className="mb-2">请填写备注：</div>
                    <Input.TextArea
                        maxLength={200}
                        showCount
                        rows={4}
                        placeholder="请输入批量内部确认备注"
                        onChange={(e) => {
                            remark = e.target.value;
                        }}
                    />
                </div>
            ),
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                if (!remark.trim()) {
                    message.warning('请先填写备注');
                    return Promise.reject(new Error('remark required'));
                }
                try {
                    const {data: resultData} = await batchConfirmRevenueBills({
                        variables: {
                            billIds: selectedRowKeys.map(String),
                            remark: remark.trim()
                        }
                    });
                    const result = resultData?.batchConfirmRevenueBills;
                    if (!result) {
                        message.error('未收到批量确认结果');
                        return Promise.reject(new Error('empty result'));
                    }
                    if (result.successCount > 0) {
                        message.success(`成功确认 ${result.successCount} 张账单`);
                        await refetch();
                        window.dispatchEvent(new Event('billDataUpdated'));
                        setSelectedRowKeys([]);
                    }
                    if (result.failureCount > 0 && result.failures?.length) {
                        Modal.warning({
                            title: `${result.failureCount} 张账单确认失败`,
                            width: 560,
                            content: (
                                <ul className="list-disc pl-5 max-h-64 overflow-y-auto text-left">
                                    {result.failures.map((failure: {billId: string; reason: string}) => (
                                        <li key={String(failure.billId)}>
                                            账单 {failure.billId}：{failure.reason}
                                        </li>
                                    ))}
                                </ul>
                            )
                        });
                    }
                } catch (error) {
                    message.error('批量确认失败: ' + (error as Error).message);
                    return Promise.reject(error as Error);
                }
            }
        });
    };

    // 处理导出账单
    const handleExportBill = (bill: SettlementBillType) => {
        setSelectedBill(bill);
        setExportModalVisible(true);
    };


    // 执行导出（带确认）
    const handleExport = async (
        template: BillExportTemplateType,
        language: ExportLanguage = 'EN'
    ) => {
        if (!selectedBill) return;

        try {
            // 先检查是否有导出历史
            message.loading({content: '正在检查导出历史...', key: 'check', duration: 0});
            const {data: historyData} = await checkExportHistory({
                variables: {
                    billId: selectedBill.id,
                    pageRequest: {page: 1, pageSize: 5}
                }
            });
            message.destroy('check');

            const historyRecords = historyData?.getExportHistory?.content || [];
            const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

            // 如果有成功的导出记录，显示确认对话框
            if (successRecords.length > 0) {
                const latestRecord = successRecords[0];
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: '确认导出账单',
                        content: (
                            <div>
                                <p>该账单已有导出记录，最近一次导出：</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>导出时间: {new Date(latestRecord.createdAt).toLocaleString('zh-CN')}</li>
                                    <li>文件名: {latestRecord.fileName}</li>
                                    <li>模板类型: {latestRecord.templateType}</li>
                                </ul>
                                <p style={{marginTop: 12, color: '#faad14'}}>
                                    <strong>提示：</strong>重新导出将生成新的文件，之前的导出记录仍会保留。
                                </p>
                                <p>确定要继续导出吗？</p>
                            </div>
                        ),
                        okText: '确定导出',
                        cancelText: '取消',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                        width: 520
                    });
                });

                if (!confirmResult) {
                    return;
                }
            } else {
                // 没有历史记录，也要确认一下
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: '确认导出账单',
                        content: (
                            <div>
                                <p>将为以下账单创建导出任务：</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>账单ID: {selectedBill.id}</li>
                                    <li>客户: {selectedBill.clientName}</li>
                                    <li>账单类型: {getBillTypeLabel(selectedBill.billType)}</li>
                                    <li>账单金额: {selectedBill.billAmount}</li>
                                </ul>
                                <p style={{marginTop: 12}}>确定要导出吗？</p>
                            </div>
                        ),
                        okText: '确定导出',
                        cancelText: '取消',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });

                if (!confirmResult) {
                    return;
                }
            }

            // 执行导出
            const result = await exportBill({
                variables: {
                    exportRequest: {
                        billId: selectedBill.id,
                        template,
                        format: 'EXCEL',
                        language
                    }
                }
            });

            if (result.data?.exportBill?.success) {
                const taskIds = result.data.exportBill.taskIds || [];
                message.success(`导出任务已创建（${taskIds.length}个文件）`);
                setExportModalVisible(false);
                // 显示进度弹窗（传入所有任务ID）
                setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
                setProgressModalVisible(true);
            } else {
                message.error(result.data?.exportBill?.errorMessage || '导出失败');
            }
        } catch (error) {
            message.destroy('check');
            message.error('导出失败: ' + (error as Error).message);
        }
    };

    // 处理导出完成
    const handleExportCompleted = (task: any) => {
        if (task.status === 'SUCCESS') {
            message.success('导出成功！');
        } else if (task.status === 'FAILED') {
            message.error(`导出失败: ${task.errorMessage || '未知错误'}`);
        }
        refetch(); // 刷新账单列表
    };

    // 格式化日期为 yyyy/mm/dd 格式
    const formatDateOnly = (timeString: string | undefined | null, timeZone: string) => {
        if (!timeString) return '-';
        try {
            // 使用统一的格式化函数，支持 UTC+XX:XX 格式
            // 统一使用横杠格式，与其他时间显示保持一致
            return formatDateWithTimezone(timeString, timeZone, 'zh-CN');
        } catch (error) {
            console.error('Date formatting error:', error);
            return timeString;
        }
    };

    // 表格列定义
    const columns = [
        {
            title: '账单ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
        },
        {
            title: '账单类型',
            dataIndex: 'billType',
            key: 'billType',
            width: 120,
            render: (type: string) => renderBillTypeTag(type),
        },
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
        },
        {
            title: 'ClientID',
            dataIndex: 'channelId',
            key: 'channelId',
            width: 180,
        },
        {
            title: '结算客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 180,
        },
        {
            title: '账单金额',
            dataIndex: 'billAmount',
            key: 'billAmount',
            width: 150,
            render: (amount: string) => <span style={{whiteSpace: 'nowrap'}}>{amount}</span>,
        },
        {
            title: '订单数量',
            dataIndex: 'orderCount',
            key: 'orderCount',
            width: 100,
            render: (_: unknown, record: SettlementBillType) => {
                const n = record.orderCount;
                if (n === null || n === undefined) return '-';
                return String(n);
            },
        },
        {
            title: '账单状态',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string, record: SettlementBillType) => {
                // 如果账单已作废，显示作废状态
                if (record.isCanceled) {
                    return <Tag color={getBillStatusColor('CANCELED')}>{getBillStatusLabel('CANCELED')}</Tag>;
                }
                return <Tag color={getBillStatusColor(status)}>{getBillStatusLabel(status)}</Tag>;
            },
        },
        {
            title: '是否已发送',
            dataIndex: 'isSent',
            key: 'isSent',
            width: 110,
            render: (sent: boolean) =>
                sent ? <Tag color="green">已发送</Tag> : <Tag>未发送</Tag>,
        },
        {
            title: '发票状态',
            dataIndex: 'invoiceStatus',
            key: 'invoiceStatus',
            width: 120,
            render: (invoiceStatus?: string | null) => {
                const config: Record<string, { color: string; text: string }> = {
                    NOT_ISSUED: {color: 'default', text: '未开具'},
                    ISSUED_NOT_SENT: {color: 'orange', text: '已开具未推送'},
                    SENT: {color: 'green', text: '已推送'},
                };
                const c = config[invoiceStatus || 'NOT_ISSUED'] || config.NOT_ISSUED;
                return <Tag color={c.color}>{c.text}</Tag>;
            },
        },
        {
            title: '账单周期',
            dataIndex: 'periodStart',
            key: 'periodRange',
            width: 280,
            render: (start: string, record: SettlementBillType) => {
                const timeZone = record.timeZone || 'UTC';
                const formattedStart = formatDateOnly(start, timeZone);
                const formattedEnd = formatDateOnly(record.periodEnd, timeZone);
                return (
                    <span style={{whiteSpace: 'nowrap'}}>
            {formattedStart} - {formattedEnd}
          </span>
                );
            },
        },
        {
            title: '时区',
            dataIndex: 'timeZone',
            key: 'timeZone',
            width: 150,
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (record: SettlementBillType) => (
                <Space>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                        <Button
                            type="link"
                            icon={<EyeOutlined/>}
                            onClick={() => handleViewDetail(record)}
                        >
                            查看详情
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
                        <Button
                            type="link"
                            icon={<ExportOutlined/>}
                            onClick={() => handleExportBill(record)}
                            disabled={!canExportBill(record)}
                            title={getExportButtonTitle(record)}
                        >
                            导出账单
                        </Button>
                    </HasResource>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
                <Card title="账单查询" className="mb-4">
                    {/* 筛选条件 */}
                    <div className="space-y-3">
                        {/* 账单ID */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单ID</label>
                            <Input
                                placeholder="输入账单ID"
                                allowClear
                                value={billIdInput}
                                onChange={(e) => setBillIdInput(e.target.value)}
                                onPressEnter={handleSearch}
                                className="w-64"
                            />
                        </div>

                        {/* 客户（多选） */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">客户</label>
                            <ClientSugSelector
                                roleType={0}
                                mode="multiple"
                                placeholder="搜索客户（可多选）..."
                                value={tempFilters.clientIds ?? []}
                                onChange={(val) => {
                                    const ids = Array.isArray(val) ? val : (val ? [val] : []);
                                    setTempFilters(prev => ({
                                        ...prev,
                                        clientIds: ids.length > 0 ? ids : undefined,
                                        clientId: undefined,
                                        clientNameKeyword: ids.length > 0 ? undefined : prev.clientNameKeyword
                                    }));
                                }}
                                className="w-64"
                            />
                        </div>

                        {/* 账单类型（本页覆盖收入侧 billType 0、1、3） */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单类型</label>
                            <Select
                                placeholder="选择账单类型"
                                allowClear
                                value={tempFilters.billType}
                                onChange={handleBillTypeChange}
                                className="w-64"
                            >
                                <Option value="DELIVERY_FEE_BILL">运费账单</Option>
                                <Option value="SLA_BILL">SLA账单</Option>
                                <Option value="SORTING_BILL">分拣账单</Option>
                            </Select>
                        </div>

                        {/* 账单状态 + 包含已作废账单 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单状态</label>
                            <Select
                                placeholder="选择账单状态"
                                allowClear
                                value={tempFilters.billStatus}
                                onChange={handleBillStatusChange}
                                className="w-64"
                            >
                                <Option value={FrontendBillStatusEnum.INTERNAL_ALREADY}>账单已出账</Option>
                                {/* <Option value={FrontendBillStatusEnum.ADJUSTMENT_PENDING}>账单调账待确认</Option> */}
                                <Option value={FrontendBillStatusEnum.INTERNAL_CONFIRMED}>账单已内部确认</Option>
                                <Option value={FrontendBillStatusEnum.EXTERNAL_REJECTED}>账单已申诉驳回</Option>
                                <Option value={FrontendBillStatusEnum.EXTERNAL_CONFIRMED}>账单已外部确认</Option>
                            </Select>
                            <label className="flex items-center cursor-pointer ml-4">
                                <input
                                    type="checkbox"
                                    checked={tempFilters.includeCanceled}
                                    onChange={(e) => {
                                        setTempFilters(prev => ({
                                            ...prev,
                                            includeCanceled: e.target.checked
                                        }));
                                    }}
                                    className="mr-2 w-4 h-4"
                                />
                                <span className="text-sm text-gray-700">包含已作废账单</span>
                            </label>
                        </div>

                        {/* 发送状态 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">发送状态</label>
                            <Select
                                placeholder="不限"
                                allowClear
                                className="w-64"
                                value={
                                    tempFilters.isSent === undefined
                                        ? undefined
                                        : tempFilters.isSent
                                            ? 'sent'
                                            : 'not_sent'
                                }
                                onChange={(v) => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        isSent: v === 'sent' ? true : v === 'not_sent' ? false : undefined
                                    }));
                                }}
                            >
                                <Option value="sent">已发送</Option>
                                <Option value="not_sent">未发送</Option>
                            </Select>
                        </div>

                        {/* 发票状态 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">发票状态</label>
                            <Select
                                placeholder="不限"
                                allowClear
                                className="w-64"
                                value={tempFilters.invoiceStatus}
                                onChange={(v) => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        invoiceStatus: v || undefined
                                    }));
                                }}
                            >
                                <Option value="NOT_ISSUED">未开具</Option>
                                <Option value="ISSUED_NOT_SENT">已开具未推送</Option>
                                <Option value="SENT">已推送</Option>
                            </Select>
                        </div>

                        {/* 账单金额范围 */}
                        <div className="flex items-center flex-wrap gap-y-2">
                            <label className="text-sm font-medium text-gray-700 w-32">账单金额</label>
                            <Space wrap>
                                <Input
                                    placeholder="最小金额"
                                    className="w-28"
                                    value={tempFilters.billAmountRange?.minAmount ?? ''}
                                    onChange={(e) =>
                                        setTempFilters(prev => ({
                                            ...prev,
                                            billAmountRange: {
                                                ...(prev.billAmountRange ?? {currency: 'USD'}),
                                                minAmount: e.target.value
                                            }
                                        }))
                                    }
                                />
                                <span className="text-gray-500">—</span>
                                <Input
                                    placeholder="最大金额"
                                    className="w-28"
                                    value={tempFilters.billAmountRange?.maxAmount ?? ''}
                                    onChange={(e) =>
                                        setTempFilters(prev => ({
                                            ...prev,
                                            billAmountRange: {
                                                ...(prev.billAmountRange ?? {currency: 'USD'}),
                                                maxAmount: e.target.value
                                            }
                                        }))
                                    }
                                />
                            </Space>
                        </div>

                        {/* 账单时间范围 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单时间范围</label>
                            <BillTimeRangePicker
                                value={tempFilters.billTimeRange}
                                onChange={handleTimeRangeChange}
                            />
                        </div>
                    </div>

                    {/* 查询按钮 */}
                    <div className="flex justify-start pt-4 mt-2 border-t border-gray-200">
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined/>}
                                onClick={handleSearch}
                                size="large"
                            >
                                查询
                            </Button>
                        </HasResource>
                    </div>
                </Card>

                <Card
                    title="账单列表"
                    extra={
                        canOperate ? (
                            <Tooltip title={selectedRowKeys.length === 0 ? '请先勾选要确认的账单' : undefined}>
                                <Button
                                    type="primary"
                                    loading={batchConfirmLoading}
                                    disabled={selectedRowKeys.length === 0}
                                    onClick={handleBatchInternalConfirm}
                                >
                                    批量内部确认账单
                                </Button>
                            </Tooltip>
                        ) : undefined
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={data?.getBillListing?.content || []}
                        loading={loading}
                        rowKey="id"
                        rowSelection={
                            canOperate
                                ? {
                                      selectedRowKeys,
                                      preserveSelectedRowKeys: true,
                                      getCheckboxProps: (record: SettlementBillType) => ({
                                          disabled:
                                              record.isCanceled ||
                                              !REVENUE_BATCH_CONFIRMABLE_STATUSES.has(record.status)
                                      }),
                                      onChange: (keys) => {
                                          if (keys.length > 100) {
                                              message.warning('单次最多选择100张账单');
                                              return;
                                          }
                                          setSelectedRowKeys(keys);
                                      }
                                  }
                                : undefined
                        }
                        pagination={false}
                        summary={() => {
                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell
                                        index={0}
                                        colSpan={columns.length + (canOperate ? 1 : 0)}
                                    >
                                        <div className="flex w-full justify-between">
                                            <span>汇总客户数：{data?.getBillListing?.summary?.customerCount ?? 0}</span>
                                            <span>汇总账单数：{data?.getBillListing?.summary?.billCount ?? 0}</span>
                                            <span>汇总账单金额：{data?.getBillListing?.summary?.totalAmount ?? '-'}</span>
                                        </div>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                        scroll={{x: 1480}}
                     className="mb-4"/>

                    {data?.getBillListing?.pageInfo && (
                        <TablePaginationBar
                            total={data.getBillListing.pageInfo.totalElements}
                            current={data.getBillListing.pageInfo.page}
                            pageSize={data.getBillListing.pageInfo.size}
                            onChange={handlePageChange}
                        />
                    )}
                </Card>

                {/* 导出模态框 */}
                <BillExportModal
                    visible={exportModalVisible}
                    bill={selectedBill}
                    onCancel={() => setExportModalVisible(false)}
                    onExport={handleExport}
                />

                {/* 导出进度弹窗 */}
                <BillExportProgressModal
                    taskId={exportTaskId}
                    visible={progressModalVisible}
                    onClose={() => {
                        setProgressModalVisible(false);
                        setExportTaskId(null);
                    }}
                    onCompleted={handleExportCompleted}
                />

                </div>
    );
};

export default SettlementBillList;

